import { Children, MouseEvent, useRef, useEffect, useState } from "react";
import css from "@styled-system/css";
import styled from "styled-components";
import { space, layout, variant } from "styled-system";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import { variants } from "./variants";
import { Highlight } from "./Step";
import {
  SubTaskAccordionProps,
  SubTaskProps,
  SubTaskVariant,
} from "./verticalTimeline.types";
import { AdditionalInfo } from "./StepHeading";
import Link from "../Link/Link";

const Task = styled(Box)<{
  variant?: SubTaskVariant;
  hasAction: boolean;
}>`
  ${(props) =>
    css({
      backgroundColor: "primary-white",
      width: "100%",
      border: "1px solid rgba(255, 255, 255, 0.47)",
      boxSizing: "border-box",
      borderRadius: "8px",
      paddingX: 5,
      margin: 0,
      boxShadow:
        "0.5px 0.5px 4px rgba(231, 232, 234, 0.25), 0.8px 0.8px 8px 0.8px rgba(231, 232, 234, 0.2)",
      "&:hover": {
        boxShadow:
          "rgb(246 246 246 / 45%) 0px 4px 14px 1px, rgb(246 246 246 / 56%) 4px 0px 14px 1px, rgb(246 246 246 / 44%) 8px 8px 20px 0.5px, rgb(241 241 241 / 43%) 2px 2px 6px 1px",
      },
      cursor: props.hasAction ? "pointer" : "auto",
    })}
  ${space}
  ${layout}
  ${variant({ variants })}
`;

const TaskHeading = styled.summary`
  ${css({
    paddingY: 5,
    display: "inline-flex",
    width: "100%",
  })}
`;

const StyledDetails = styled.details<{ contentHeight: number | string }>`
  ${(props) => `transition: height 0.3s ease-in-out;
  overflow: hidden;
  height: 60px;

  summary::-webkit-details-marker {
    display: none;
  }

  &[open] {
    height: ${props.contentHeight}px;
  }`}
`;

const SubTaskAccordion = ({
  defaultExpanded,
  onToggle,
  completed = false,
  title,
  additionalInfo,
  children,
  setTasksSizes,
  setShouldRecalculateHeight,
  stepExpanded,
  index = 0,
  tasksSizes,
  onChange,
  titleLinkProps,
}: SubTaskAccordionProps) => {
  const [taskExpanded, setTaskExpanded] = useState<boolean>(defaultExpanded);
  const handleTaskClick = (e: MouseEvent<HTMLDetailsElement>) => {
    if (onToggle && !completed && e.currentTarget.open) {
      e.preventDefault();
      onToggle();
      return;
    }

    const detailsElement = e.currentTarget.parentElement as HTMLDetailsElement;
    const isExpanded = !detailsElement.open;
    setTaskExpanded(isExpanded);
    if (isExpanded && setShouldRecalculateHeight) {
      setShouldRecalculateHeight(true);
    }
  };

  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const detailsNode = detailsRef.current;

    if (detailsNode && setTasksSizes && stepExpanded) {
      const taskSizes = detailsNode.scrollHeight + 2;

      setTasksSizes((prevState) => {
        const newState: number[] = [...prevState];
        const oldValue = prevState[index];
        if (oldValue) {
          newState[index] = taskSizes > oldValue ? taskSizes : oldValue;
        } else {
          newState[index] = taskSizes;
        }
        return newState;
      });
    }
  }, [setTasksSizes, taskExpanded, stepExpanded, index]);

  const numberOfChildren = Children.count(children);
  const contentHeight =
    tasksSizes && numberOfChildren > 0 ? tasksSizes[index] : "auto";

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    const detailsElement = e.currentTarget.parentElement as HTMLDetailsElement;

    if (onChange) {
      onChange(detailsElement.open ? "collapse" : "expand");
    }
  };

  return (
    <StyledDetails
      open={defaultExpanded}
      onClick={handleTaskClick}
      contentHeight={contentHeight}
      ref={detailsRef}
    >
      <TaskHeading onClick={handleClick}>
        <Typography variant="h6" color="footer-blue" marginY={0} css="flex:1">
          {titleLinkProps ? (
            <Link variant="inherit" {...titleLinkProps}>
              {title}
            </Link>
          ) : (
            title
          )}
        </Typography>
        {additionalInfo && (
          <AdditionalInfo
            variant="body"
            color="grey-blue"
            htmlTag="span"
            margin={0}
          >
            {additionalInfo}
          </AdditionalInfo>
        )}
      </TaskHeading>
      <Box paddingBottom={numberOfChildren > 0 ? 5 : 0}>{children}</Box>
    </StyledDetails>
  );
};

export const SubTask = ({
  children,
  completed,
  onToggle,
  variant = "default",
  ...props
}: SubTaskProps) => {
  const taskTopMargin =
    props.index === 0 && props.numberOfLines && props.numberOfLines > 1 ? 2 : 0;

  return (
    <Box display="flex" mb={4} css="z-index: 1;">
      <Box display="inline-flex" minWidth={30} justifyContent="center">
        <Highlight variant={variant} />
      </Box>
      <Task
        flex={1}
        variant={variant}
        hasAction={Boolean(onToggle) && !completed}
        marginTop={taskTopMargin}
      >
        <SubTaskAccordion
          {...props}
          onToggle={onToggle}
          completed={completed}
          defaultExpanded={!completed}
        >
          {children}
        </SubTaskAccordion>
      </Task>
    </Box>
  );
};

export default SubTask;
